import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Loader2, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const EXIT_INTENT_KEY = "sdc_exit_popup_shown";

const ExitIntentPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { toast } = useToast();
  const [formData, setFormData] = useState({ name: "", email: "", phone: "" });
  const [honeypot, setHoneypot] = useState("");

  const handleMouseLeave = useCallback((e: MouseEvent) => {
    if (e.clientY <= 5 && !sessionStorage.getItem(EXIT_INTENT_KEY)) {
      setIsOpen(true);
      sessionStorage.setItem(EXIT_INTENT_KEY, "true");
    }
  }, []);

  useEffect(() => {
    // Only show on desktop (exit intent doesn't work on mobile)
    if (window.innerWidth < 768) return;
    // Delay adding listener so it doesn't trigger immediately
    const timer = setTimeout(() => {
      document.addEventListener("mouseleave", handleMouseLeave);
    }, 5000);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [handleMouseLeave]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (honeypot) { setIsSubmitted(true); return; }

    setIsSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke("submit-contact-form", {
        body: {
          ...formData,
          subject: "Free Fleet Assessment Request",
          message: "User requested a free fleet assessment via exit-intent popup.",
          honeypot,
        },
      });
      if (error) throw new Error(error.message);
      setIsSubmitted(true);
      toast({ title: "Request Received!", description: "We'll contact you shortly for your free fleet assessment." });
    } catch (err) {
      toast({ title: "Error", description: "Failed to submit. Please try again.", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={() => setIsOpen(false)} />

          {/* Modal */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25 }}
            className="relative w-full max-w-md rounded-2xl border border-primary/20 bg-card shadow-2xl shadow-primary/10"
          >
            <button
              onClick={() => setIsOpen(false)}
              className="absolute top-4 right-4 p-1.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="p-8">
              {isSubmitted ? (
                <div className="text-center py-6">
                  <div className="w-16 h-16 rounded-full bg-eco-green/20 flex items-center justify-center mx-auto mb-4">
                    <Gift className="w-8 h-8 text-eco-green" />
                  </div>
                  <h3 className="text-2xl font-bold text-foreground mb-2">Thank You!</h3>
                  <p className="text-muted-foreground">Our team will reach out within 24 hours to schedule your free fleet assessment.</p>
                  <Button className="mt-6" onClick={() => setIsOpen(false)}>Continue Browsing</Button>
                </div>
              ) : (
                <>
                  <div className="text-center mb-6">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary to-amber-glow flex items-center justify-center mx-auto mb-4">
                      <Gift className="w-7 h-7 text-primary-foreground" />
                    </div>
                    <h3 className="text-2xl font-bold text-foreground mb-2">Wait! Don't Miss Out</h3>
                    <p className="text-muted-foreground">
                      Get a <span className="text-primary font-semibold">free fleet assessment</span> — we'll help you find the perfect mobility solution for your organization.
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-3">
                    <Input
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="h-12 bg-background/50 border-border/50"
                    />
                    <Input
                      type="email"
                      placeholder="Email Address"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="h-12 bg-background/50 border-border/50"
                    />
                    <Input
                      type="tel"
                      placeholder="Phone Number"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="h-12 bg-background/50 border-border/50"
                    />

                    <div className="absolute -left-[9999px] opacity-0 pointer-events-none" aria-hidden="true">
                      <Input type="text" name="website" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full h-12 bg-gradient-to-r from-eco-green to-teal-bright hover:from-eco-green-light hover:to-teal-bright text-white font-semibold"
                    >
                      {isSubmitting ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                      ) : (
                        <><Send className="w-4 h-4 mr-2" />Get Free Assessment</>
                      )}
                    </Button>

                    <p className="text-xs text-center text-muted-foreground">No spam. We respect your privacy.</p>
                  </form>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ExitIntentPopup;
